
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import V2Layout from '@/components/v2/V2Layout';
import TreatmentPlannerV2 from '@/pages/v2/patients/TreatmentPlannerV2';
import PatientDashboardV2 from '@/pages/v2/patients/PatientDashboardV2';
import GlowWalletV2 from '@/pages/v2/patients/GlowWalletV2';
import ReferralSystemV2 from '@/pages/v2/patients/ReferralSystemV2';
import MembershipPortalV2 from '@/pages/v2/patients/MembershipPortalV2';

// Import individual route components instead of AppRoutes wrapper
import { AuthRoutes } from '@/routes/AuthRoutes';
import { TreatmentRoutes } from '@/routes/TreatmentRoutes';
import { ConditionRoutes } from '@/routes/ConditionRoutes';
import { CheckoutRoutes } from '@/routes/CheckoutRoutes';
import { StaffRoutes } from '@/routes/StaffRoutes';
import { EnhancedStaffRoutes } from '@/routes/EnhancedStaffRoutes';
import { PatientRoutes } from '@/routes/PatientRoutes';
import { HQRoutes } from '@/routes/HQRoutes';
import { AcademyRoutes } from '@/routes/AcademyRoutes';
import { AIAndAdminRoutes } from '@/routes/AIAndAdminRoutes';
import { AppMobileRoutes } from '@/routes/AppMobileRoutes';

// Import individual pages
import AppLanding from '@/pages/app/AppLanding';
import AppLogin from '@/pages/app/AppLogin';
import Index from '@/pages/Index';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import Treatments from '@/pages/Treatments';
import Conditions from '@/pages/Conditions';
import Locations from '@/pages/Locations';
import Membership from '@/pages/Membership';
import FAQs from '@/pages/FAQs';
import Sitemap from '@/pages/Sitemap';
import Quiz from '@/pages/Quiz';
import Partner from '@/pages/Partner';
import BrandKit from '@/pages/BrandKit';
import ERM from '@/pages/ERM';
import NotFound from '@/pages/NotFound';
import Dashboard from '@/pages/Dashboard';
import ProtectedRoute from '@/components/ProtectedRoute';
import Login from '@/pages/Login';
import PatientLogin from '@/pages/login/PatientLogin';
import StaffLogin from '@/pages/login/StaffLogin';
import HQLogin from '@/pages/login/HQLogin';
import Menu from '@/pages/Menu';

function App() {
  const queryClient = new QueryClient();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <div className="App">
              <Routes>
                {/* V2 routes */}
                <Route path="/v2" element={<V2Layout />}>
                  <Route path="patients/dashboard" element={<PatientDashboardV2 />} />
                  <Route path="patients/planner" element={<TreatmentPlannerV2 />} />
                  <Route path="patients/wallet" element={<GlowWalletV2 />} />
                  <Route path="patients/refer" element={<ReferralSystemV2 />} />
                  <Route path="patients/membership" element={<MembershipPortalV2 />} />
                  <Route index element={
                    <div className="text-center py-16">
                      <h1 className="text-4xl font-bold text-juv-deep-chestnut mb-4">
                        Welcome to JUV 2.0
                      </h1>
                      <p className="text-lg text-juv-deep-chestnut/70 mb-8">
                        Choose your path to get started
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                        <Button asChild className="h-20 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                          <a href="/v2/patients/dashboard">Patient Dashboard</a>
                        </Button>
                        <Button asChild className="h-20 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                          <a href="/v2/patients/planner">Treatment Planner</a>
                        </Button>
                        <Button asChild className="h-20 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                          <a href="/v2/patients/wallet">Glow Wallet</a>
                        </Button>
                      </div>
                    </div>
                  } />
                </Route>

                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/treatments" element={<Treatments />} />
                <Route path="/conditions" element={<Conditions />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/faq" element={<FAQs />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/partner" element={<Partner />} />
                <Route path="/brand-kit" element={<BrandKit />} />
                <Route path="/erm" element={<ERM />} />

                {/* Login Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/login/patient" element={<PatientLogin />} />
                <Route path="/login/staff" element={<StaffLogin />} />
                <Route path="/login/hq" element={<HQLogin />} />

                {/* Dashboard - Protected */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />

                {/* App Landing - Role-based redirect */}
                <Route path="/app" element={<AppLanding />} />
                
                {/* App Login */}
                <Route path="/app/login" element={<AppLogin />} />

                {/* Redirects from old operations paths */}
                <Route path="/ops" element={<Navigate to="/hq" replace />} />
                <Route path="/ops/dashboard" element={<Navigate to="/hq/dashboard" replace />} />
                <Route path="/ops/studio/overview" element={<Navigate to="/hq/studios" replace />} />
                <Route path="/ops/kpi-tracker" element={<Navigate to="/hq/metrics" replace />} />
                <Route path="/ops/staff-tracker" element={<Navigate to="/hq/staff" replace />} />
                <Route path="/ops/tasks" element={<Navigate to="/hq/tasks" replace />} />
                <Route path="/ops/checklists" element={<Navigate to="/hq/checklists" replace />} />
                <Route path="/ops/support" element={<Navigate to="/hq/support" replace />} />
                <Route path="/ops/sop" element={<Navigate to="/hq/sops" replace />} />
                <Route path="/ops/sop/:category" element={<Navigate to="/hq/sops" replace />} />
                <Route path="/ops/sop/:category/:docId" element={<Navigate to="/hq/sops" replace />} />
                <Route path="/ops/sop/new" element={<Navigate to="/hq/sops/new" replace />} />
                <Route path="/ops/sop/editor" element={<Navigate to="/hq/sops" replace />} />
                <Route path="/operations" element={<Navigate to="/hq" replace />} />
                <Route path="/operations/launch" element={<Navigate to="/hq/checklists" replace />} />
                <Route path="/operations/partner-portal" element={<Navigate to="/hq" replace />} />

                {/* Redirects from old app/hq paths */}
                <Route path="/app/hq" element={<Navigate to="/hq" replace />} />
                <Route path="/app/hq/dashboard" element={<Navigate to="/hq/dashboard" replace />} />
                <Route path="/app/hq/locations" element={<Navigate to="/hq/studios" replace />} />
                <Route path="/app/hq/sops" element={<Navigate to="/hq/sops" replace />} />
                <Route path="/app/hq/agents" element={<Navigate to="/hq/agents" replace />} />

                {/* Route groups - these return individual Route elements, not Routes wrappers */}
                {AuthRoutes()}
                {TreatmentRoutes()}
                {ConditionRoutes()}
                {CheckoutRoutes()}
                {StaffRoutes()}
                {EnhancedStaffRoutes()}
                {PatientRoutes()}
                {HQRoutes()}
                {AcademyRoutes()}
                {AIAndAdminRoutes()}
                {AppMobileRoutes()}

                {/* 404 - Not Found */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
